import copy from "clipboard-copy"
import styled, { css } from "styled-components"
import { useContext, useEffect, useState } from "react"
import { useRouter } from "next/router"
import { useT } from "react-polyglot-hooks"
import Icon from "components/icons/icon"
import Typography from "components/styled/Typography"
import { CarContext } from "scenes/CarScene/CarContext"
import { Letter } from "components/icons/letter"
import { Whatsapp } from "components/icons/whatsapp"
import { baseButton } from "components/styled/Buttons/PrimaryButton"
import { color, mq } from "helpers/styled"
import { sendEvent } from "scripts/GoogleAnalytics"
import useMediaQuery from "hooks/useMediaQuery"

const Button = styled.button(
  ({ border, fluid, color: c = "deepPink" }) => css`
    ${baseButton};

    align-items: center;
    flex-wrap: wrap;
    width: ${fluid ? "100%" : "auto"};
    max-height: 38px;
    border: 1px solid ${c};
    padding: 4px 15px;
    font-size: 12px;

    ${border
      ? `
          background: transparent;
          color: ${c};
        `
      : `
          background: ${c};
          color: ${color("white")};
        `};

    transition: color 0.2s, background-color 0.2s, border-color 0.2s;

    &:hover {
      ${border
        ? `
            background: ${c};
            color: ${color("white")};
          `
        : `
            background: transparent;
            color: ${c};
          `};
    }

    &:disabled {
      color: ${color("gray")};
      background: ${color("paleGray")};
      border-color: ${color("paleGray")};
      box-shadow: none;
    }

    > svg {
      margin-right: 5px;
      flex: 0 0 auto;
    }
  `
)

const Grid = styled.div`
  display: inline-grid;
  gap: 15px;
  grid-template-columns: 1fr 1fr;
  grid-template-areas: "a b" "c c";
  width: 100%;

  @media ${mq("md")} {
    grid-template-columns: 1fr 1fr 2fr;
    grid-template-areas: "a b c";
  }

  @media ${mq("lg")} {
    gap: 25px;
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "a b" "c c";
    max-width: 375px;
  }

  @media ${mq("xl")} {
    grid-template-areas: "a b c";
    grid-template-columns: 1fr 1fr 2fr;
    max-width: 100%;
  }

  > ${Button} {
    margin: 0;
    padding-left: 5px !important;
    padding-right: 5px !important;

    &:last-child {
      grid-area: c;
    }
  }
`

export default function Social() {
  const t = useT()
  const router = useRouter()
  const [currentUrl, setCurrentUrl] = useState("")
  const [hasNativeShare, setHasNativeShare] = useState(false)
  const isDesktop = useMediaQuery(mq("lg"))
  const {
    page,
    car: { brand, model, version }
  } = useContext(CarContext)

  const emailSubject = t("car_data.sharing.email_subject", { brand, model })

  useEffect(() => {
    setCurrentUrl(`https://swipcar.com${router.asPath}`)
    setHasNativeShare(!!navigator.share)
  }, [])

  const sendShareEvent = label => {
    sendEvent("share car", {
      event_category: page,
      event_label: label
    })
  }

  const handleShare = () => {
    sendShareEvent("share")

    if (hasNativeShare) {
      navigator
        .share({
          title: t("car_data.seo.title", { brand, model, version: version.replace('"', "") }),
          text: emailSubject,
          url: currentUrl
        })
        .then(() => {
          /*
            Shared successfully
            Note: This doesn't run on iOS, so don't do anything here
          */
        })
        .catch(() => {
          /*
            An exception is thrown upon canceling the share dialog multiple
            times. Multiple cancels block the share function from working
            entirely, which also throws an exception.

            So we ignore them
          */
        })
    }
  }

  const handleCopy = () => {
    sendShareEvent("copy")
    copy(currentUrl)
    // eslint-disable-next-line no-alert
    window.alert(t("car_data.sharing.copy_message"))
  }

  return (
    <div>
      <Typography variant="title3" weight="medium">
        {t("car_data.sharing.title")}
      </Typography>
      <Grid>
        <Button
          color={color("skyBlue")}
          border
          size="md"
          as="a"
          onClick={() => sendShareEvent("email")}
          target="_blank"
          rel="noreferrer noopener"
          href={`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${currentUrl}`}>
          <Icon Icon={Letter} />
          {t("car_data.sharing.email")}
        </Button>
        <Button
          color={color("lightGreen")}
          border
          size="md"
          as="a"
          href={`https://wa.me/?text=${currentUrl}`}
          onClick={() => sendShareEvent("whatsapp")}
          target="_blank"
          rel="noreferrer noopener">
          <Icon Icon={Whatsapp} />
          {t("car_data.sharing.whatsapp")}
        </Button>
        {!isDesktop && hasNativeShare ? (
          <Button color={color("skyBlue")} fluid size="md" css="grid-column: span 2" onClick={handleShare}>
            {t("common.share")}
          </Button>
        ) : (
          <Button color={color("skyBlue")} fluid size="md" css="grid-column: span 2" onClick={handleCopy}>
            {t("car_data.sharing.copy")}
          </Button>
        )}
      </Grid>
    </div>
  )
}
